/**
 * algodex/algodex-hooks 1.5.7
 *
 * Copyright (c)  Algodex VASP (BVI) Corp., 2022
 *
 * All Rights Reserved.
 *
 * @license BSL
 */
'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var AlgodexApi = require('@algodex/algodex-sdk/lib/AlgodexApi.js');
var PropTypes = require('prop-types');
var signer = require('@algodex/algodex-sdk/lib/wallet/signers/MyAlgoConnect');
var QRCodeModal = require('algorand-walletconnect-qrcode-modal');
var lang = require('lodash/lang');
var events = require('@algodex/algodex-sdk/lib/events');
var styled = require('@emotion/styled');
var react = require('@emotion/react');
var reactQuery = require('react-query');
var Big = require('big.js');
var floatToFixed = require('@algodex/algodex-sdk/lib/utils/format/floatToFixed');
var millify = require('millify');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

function _interopNamespace(e) {
  if (e && e.__esModule) return e;
  var n = Object.create(null);
  if (e) {
    Object.keys(e).forEach(function (k) {
      if (k !== 'default') {
        var d = Object.getOwnPropertyDescriptor(e, k);
        Object.defineProperty(n, k, d.get ? d : {
          enumerable: true,
          get: function () { return e[k]; }
        });
      }
    });
  }
  n["default"] = e;
  return Object.freeze(n);
}

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var AlgodexApi__default = /*#__PURE__*/_interopDefaultLegacy(AlgodexApi);
var PropTypes__default = /*#__PURE__*/_interopDefaultLegacy(PropTypes);
var signer__default = /*#__PURE__*/_interopDefaultLegacy(signer);
var QRCodeModal__default = /*#__PURE__*/_interopDefaultLegacy(QRCodeModal);
var events__default = /*#__PURE__*/_interopDefaultLegacy(events);
var styled__default = /*#__PURE__*/_interopDefaultLegacy(styled);
var Big__default = /*#__PURE__*/_interopDefaultLegacy(Big);
var floatToFixed__default = /*#__PURE__*/_interopDefaultLegacy(floatToFixed);
var millify__default = /*#__PURE__*/_interopDefaultLegacy(millify);

/*
 * Algodex Hooks
 * Copyright (C) 2021-2022 Algodex VASP (BVI) Corp.
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.

 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */
/**
 * @typedef import('@algodex/algodex-sdk')
 */

/**
 *
 * @type {React.Context<{}>}
 */

var AlgodexContext = /*#__PURE__*/React.createContext({});
/**
 *
 * @param {Object} props Component Properties
 * @param {AlgodexApi} props.dex The AlgodexAPI Instance
 * @param {JSX.Element} [props.children] Component Children
 * @return {JSX.Element}
 */

function Provider(_ref) {
  var children = _ref.children,
      dex = _ref.dex;
  var context = React.useMemo(function () {
    return dex;
  }, [dex]);
  return /*#__PURE__*/React__default["default"].createElement(AlgodexContext.Provider, {
    value: context
  }, children);
}
Provider.propTypes = {
  /**
   * Children Components
   */
  children: PropTypes__default["default"].node,

  /**
   * Instance of a AlgodexAPI
   */
  dex: PropTypes__default["default"].instanceOf(AlgodexApi__default["default"])
};

/*
 * Algodex Hooks
 * Copyright (C) 2021-2022 Algodex VASP (BVI) Corp.
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.

 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */
/**
 * @typedef {Object} AlgodexAPIHook
 * @property {AlgodexApi|boolean} algodex AlgodexAPI Instance or false
 * @property {boolean} isConnected Has connected wallets
 * @property {function} setWallet Set Wallets
 * @property {function} setAsset Set Asset
 * @property {function} setConfig Set AlgodexAPIConfig
 * @property {function} setAddresses Set Available Addresses
 * @property {((function(): Promise<void>)|*)} connect MyAlgo Connect
 */

/**
 * useAlgodexAPI
 *
 * Hooks for working with the algodex context. It constructs
 * an instance of AlgodexAPI
 *
 * @return {AlgodexAPIHook}
 */

function useAlgodex() {
  // Get AlgodexAPI Context
  var algodex = React.useContext(AlgodexContext); // Check connection status

  var isConnected = typeof algodex !== 'undefined' && typeof algodex.wallet !== 'undefined' && typeof algodex.wallet.address !== 'undefined' && typeof algodex.wallet.connector !== 'undefined' && algodex.wallet.connector.connected; // Return Algodex

  return {
    algodex: algodex,
    isConnected: isConnected,
    http: algodex.http,
    wallet: algodex.wallet,
    setWallet: function setWallet() {
      return algodex.setWallet.apply(algodex, arguments);
    },
    placeOrder: function placeOrder() {
      return algodex.placeOrder.apply(algodex, arguments);
    }
  };
}

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

var runtime = {exports: {}};

/**
 * Copyright (c) 2014-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

(function (module) {
var runtime = (function (exports) {

  var Op = Object.prototype;
  var hasOwn = Op.hasOwnProperty;
  var undefined$1; // More compressible than void 0.
  var $Symbol = typeof Symbol === "function" ? Symbol : {};
  var iteratorSymbol = $Symbol.iterator || "@@iterator";
  var asyncIteratorSymbol = $Symbol.asyncIterator || "@@asyncIterator";
  var toStringTagSymbol = $Symbol.toStringTag || "@@toStringTag";

  function define(obj, key, value) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
    return obj[key];
  }
  try {
    // IE 8 has a broken Object.defineProperty that only works on DOM objects.
    define({}, "");
  } catch (err) {
    define = function(obj, key, value) {
      return obj[key] = value;
    };
  }

  function wrap(innerFn, outerFn, self, tryLocsList) {
    // If outerFn provided and outerFn.prototype is a Generator, then outerFn.prototype instanceof Generator.
    var protoGenerator = outerFn && outerFn.prototype instanceof Generator ? outerFn : Generator;
    var generator = Object.create(protoGenerator.prototype);
    var context = new Context(tryLocsList || []);

    // The ._invoke method unifies the implementations of the .next,
    // .throw, and .return methods.
    generator._invoke = makeInvokeMethod(innerFn, self, context);

    return generator;
  }
  exports.wrap = wrap;

  // Try/catch helper to minimize deoptimizations. Returns a completion
  // record like context.tryEntries[i].completion. This interface could
  // have been (and was previously) designed to take a closure to be
  // invoked without arguments, but in all the cases we care about we
  // already have an existing method we want to call, so there's no need
  // to create a new function object. We can even get away with assuming
  // the method takes exactly one argument, since that happens to be true
  // in every case, so we don't have to touch the arguments object. The
  // only additional allocation required is the completion record, which
  // has a stable shape and so hopefully should be cheap to allocate.
  function tryCatch(fn, obj, arg) {
    try {
      return { type: "normal", arg: fn.call(obj, arg) };
    } catch (err) {
      return { type: "throw", arg: err };
    }
  }

  var GenStateSuspendedStart = "suspendedStart";
  var GenStateSuspendedYield = "suspendedYield";
  var GenStateExecuting = "executing";
  var GenStateCompleted = "completed";

  // Returning this object from the innerFn has the same effect as
  // breaking out of the dispatch switch statement.
  var ContinueSentinel = {};

  // Dummy constructor functions that we use as the .constructor and
  // .constructor.prototype properties for functions that return Generator
  // objects. For full spec compliance, you may wish to configure your
  // minifier not to mangle the names of these two functions.
  function Generator() {}
  function GeneratorFunction() {}
  function GeneratorFunctionPrototype() {}

  // This is a polyfill for %IteratorPrototype% for environments that
  // don't natively support it.
  var IteratorPrototype = {};
  define(IteratorPrototype, iteratorSymbol, function () {
    return this;
  });

  var getProto = Object.getPrototypeOf;
  var NativeIteratorPrototype = getProto && getProto(getProto(values([])));
  if (NativeIteratorPrototype &&
      NativeIteratorPrototype !== Op &&
      hasOwn.call(NativeIteratorPrototype, iteratorSymbol)) {
    // This environment has a native %IteratorPrototype%; use it instead
    // of the polyfill.
    IteratorPrototype = NativeIteratorPrototype;
  }

  var Gp = GeneratorFunctionPrototype.prototype =
    Generator.prototype = Object.create(IteratorPrototype);
  GeneratorFunction.prototype = GeneratorFunctionPrototype;
  define(Gp, "constructor", GeneratorFunctionPrototype);
  define(GeneratorFunctionPrototype, "constructor", GeneratorFunction);
  GeneratorFunction.displayName = define(
    GeneratorFunctionPrototype,
    toStringTagSymbol,
    "GeneratorFunction"
  );

  // Helper for defining the .next, .throw, and .return methods of the
  // Iterator interface in terms of a single ._invoke method.
  function defineIteratorMethods(prototype) {
    ["next", "throw", "return"].forEach(function(method) {
      define(prototype, method, function(arg) {
        return this._invoke(method, arg);
      });
    });
  }

  exports.isGeneratorFunction = function(genFun) {
    var ctor = typeof genFun === "function" && genFun.constructor;
    return ctor
      ? ctor === GeneratorFunction ||
        // For the native GeneratorFunction constructor, the best we can
        // do is to check its .name property.
        (ctor.displayName || ctor.name) === "GeneratorFunction"
      : false;
  };

  exports.mark = function(genFun) {
    if (Object.setPrototypeOf) {
      Object.setPrototypeOf(genFun, GeneratorFunctionPrototype);
    } else {
      genFun.__proto__ = GeneratorFunctionPrototype;
      define(genFun, toStringTagSymbol, "GeneratorFunction");
    }
    genFun.prototype = Object.create(Gp);
    return genFun;
  };

  // Within the body of any async function, `await x` is transformed to
  // `yield regeneratorRuntime.awrap(x)`, so that the runtime can test
  // `hasOwn.call(value, "__await")` to determine if the yielded value is
  // meant to be awaited.
  exports.awrap = function(arg) {
    return { __await: arg };
  };

  function AsyncIterator(generator, PromiseImpl) {
    function invoke(method, arg, resolve, reject) {
      var record = tryCatch(generator[method], generator, arg);
      if (record.type === "throw") {
        reject(record.arg);
      } else {
        var result = record.arg;
        var value = result.value;
        if (value &&
            typeof value === "object" &&
            hasOwn.call(value, "__await")) {
          return PromiseImpl.resolve(value.__await).then(function(value) {
            invoke("next", value, resolve, reject);
          }, function(err) {
            invoke("throw", err, resolve, reject);
          });
        }

        return PromiseImpl.resolve(value).then(function(unwrapped) {
          // When a yielded Promise is resolved, its final value becomes
          // the .value of the Promise<{value,done}> result for the
          // current iteration.
          result.value = unwrapped;
          resolve(result);
        }, function(error) {
          // If a rejected Promise was yielded, throw the rejection back
          // into the async generator function so it can be handled there.
          return invoke("throw", error, resolve, reject);
        });
      }
    }

    var previousPromise;

    function enqueue(method, arg) {
      function callInvokeWithMethodAndArg() {
        return new PromiseImpl(function(resolve, reject) {
          invoke(method, arg, resolve, reject);
        });
      }

      return previousPromise =
        // If enqueue has been called before, then we want to wait until
        // all previous Promises have been resolved before calling invoke,
        // so that results are always delivered in the correct order. If
        // enqueue has not been called before, then it is important to
        // call invoke immediately, without waiting on a callback to fire,
        // so that the async generator function has the opportunity to do
        // any necessary setup in a predictable way. This predictability
        // is why the Promise constructor synchronously invokes its
        // executor callback, and why async functions synchronously
        // execute code before the first await. Since we implement simple
        // async functions in terms of async generators, it is especially
        // important to get this right, even though it requires care.
        previousPromise ? previousPromise.then(
          callInvokeWithMethodAndArg,
          // Avoid propagating failures to Promises returned by later
          // invocations of the iterator.
          callInvokeWithMethodAndArg
        ) : callInvokeWithMethodAndArg();
    }

    // Define the unified helper method that is used to implement .next,
    // .throw, and .return (see defineIteratorMethods).
    this._invoke = enqueue;
  }

  defineIteratorMethods(AsyncIterator.prototype);
  define(AsyncIterator.prototype, asyncIteratorSymbol, function () {
    return this;
  });
  exports.AsyncIterator = AsyncIterator;

  // Note that simple async functions are implemented on top of
  // AsyncIterator objects; they just return a Promise for the value of
  // the final result produced by the iterator.
  exports.async = function(innerFn, outerFn, self, tryLocsList, PromiseImpl) {
    if (PromiseImpl === void 0) PromiseImpl = Promise;

    var iter = new AsyncIterator(
      wrap(innerFn, outerFn, self, tryLocsList),
      PromiseImpl
    );

    return exports.isGeneratorFunction(outerFn)
      ? iter // If outerFn is a generator, return the full iterator.
      : iter.next().then(function(result) {
          return result.done ? result.value : iter.next();
        });
  };

  function makeInvokeMethod(innerFn, self, context) {
    var state = GenStateSuspendedStart;

    return function invoke(method, arg) {
      if (state === GenStateExecuting) {
        throw new Error("Generator is already running");
      }

      if (state === GenStateCompleted) {
        if (method === "throw") {
          throw arg;
        }

        // Be forgiving, per 25.3.3.3.3 of the spec:
        // https://people.mozilla.org/~jorendorff/es6-draft.html#sec-generatorresume
        return doneResult();
      }

      context.method = method;
      context.arg = arg;

      while (true) {
        var delegate = context.delegate;
        if (delegate) {
          var delegateResult = maybeInvokeDelegate(delegate, context);
          if (delegateResult) {
            if (delegateResult === ContinueSentinel) continue;
            return delegateResult;
          }
        }

        if (context.method === "next") {
          // Setting context._sent for legacy support of Babel's
          // function.sent implementation.
          context.sent = context._sent = context.arg;

        } else if (context.method === "throw") {
          if (state === GenStateSuspendedStart) {
            state = GenStateCompleted;
            throw context.arg;
          }

          context.dispatchException(context.arg);

        } else if (context.method === "return") {
          context.abrupt("return", context.arg);
        }

        state = GenStateExecuting;

        var record = tryCatch(innerFn, self, context);
        if (record.type === "normal") {
          // If an exception is thrown from innerFn, we leave state ===
          // GenStateExecuting and loop back for another invocation.
          state = context.done
            ? GenStateCompleted
            : GenStateSuspendedYield;

          if (record.arg === ContinueSentinel) {
            continue;
          }

          return {
            value: record.arg,
            done: context.done
          };

        } else if (record.type === "throw") {
          state = GenStateCompleted;
          // Dispatch the exception by looping back around to the
          // context.dispatchException(context.arg) call above.
          context.method = "throw";
          context.arg = record.arg;
        }
      }
    };
  }

  // Call delegate.iterator[context.method](context.arg) and handle the
  // result, either by returning a { value, done } result from the
  // delegate iterator, or by modifying context.method and context.arg,
  // setting context.delegate to null, and returning the ContinueSentinel.
  function maybeInvokeDelegate(delegate, context) {
    var method = delegate.iterator[context.method];
    if (method === undefined$1) {
      // A .throw or .return when the delegate iterator has no .throw
      // method always terminates the yield* loop.
      context.delegate = null;

      if (context.method === "throw") {
        // Note: ["return"] must be used for ES3 parsing compatibility.
        if (delegate.iterator["return"]) {
          // If the delegate iterator has a return method, give it a
          // chance to clean up.
          context.method = "return";
          context.arg = undefined$1;
          maybeInvokeDelegate(delegate, context);

          if (context.method === "throw") {
            // If maybeInvokeDelegate(context) changed context.method from
            // "return" to "throw", let that override the TypeError below.
            return ContinueSentinel;
          }
        }

        context.method = "throw";
        context.arg = new TypeError(
          "The iterator does not provide a 'throw' method");
      }

      return ContinueSentinel;
    }

    var record = tryCatch(method, delegate.iterator, context.arg);

    if (record.type === "throw") {
      context.method = "throw";
      context.arg = record.arg;
      context.delegate = null;
      return ContinueSentinel;
    }

    var info = record.arg;

    if (! info) {
      context.method = "throw";
      context.arg = new TypeError("iterator result is not an object");
      context.delegate = null;
      return ContinueSentinel;
    }

    if (info.done) {
      // Assign the result of the finished delegate to the temporary
      // variable specified by delegate.resultName (see delegateYield).
      context[delegate.resultName] = info.value;

      // Resume execution at the desired location (see delegateYield).
      context.next = delegate.nextLoc;

      // If context.method was "throw" but the delegate handled the
      // exception, let the outer generator proceed normally. If
      // context.method was "next", forget context.arg since it has been
      // "consumed" by the delegate iterator. If context.method was
      // "return", allow the original .return call to continue in the
      // outer generator.
      if (context.method !== "return") {
        context.method = "next";
        context.arg = undefined$1;
      }

    } else {
      // Re-yield the result returned by the delegate method.
      return info;
    }

    // The delegate iterator is finished, so forget it and continue with
    // the outer generator.
    context.delegate = null;
    return ContinueSentinel;
  }

  // Define Generator.prototype.{next,throw,return} in terms of the
  // unified ._invoke helper method.
  defineIteratorMethods(Gp);

  define(Gp, toStringTagSymbol, "Generator");

  // A Generator should always return itself as the iterator object when the
  // @@iterator function is called on it. Some browsers' implementations of the
  // iterator prototype chain incorrectly implement this, causing the Generator
  // object to not be returned from this call. This ensures that doesn't happen.
  // See https://github.com/facebook/regenerator/issues/274 for more details.
  define(Gp, iteratorSymbol, function() {
    return this;
  });

  define(Gp, "toString", function() {
    return "[object Generator]";
  });

  function pushTryEntry(locs) {
    var entry = { tryLoc: locs[0] };

    if (1 in locs) {
      entry.catchLoc = locs[1];
    }

    if (2 in locs) {
      entry.finallyLoc = locs[2];
      entry.afterLoc = locs[3];
    }

    this.tryEntries.push(entry);
  }

  function resetTryEntry(entry) {
    var record = entry.completion || {};
    record.type = "normal";
    delete record.arg;
    entry.completion = record;
  }

  function Context(tryLocsList) {
    // The root entry object (effectively a try statement without a catch
    // or a finally block) gives us a place to store values thrown from
    // locations where there is no enclosing try statement.
    this.tryEntries = [{ tryLoc: "root" }];
    tryLocsList.forEach(pushTryEntry, this);
    this.reset(true);
  }

  exports.keys = function(object) {
    var keys = [];
    for (var key in object) {
      keys.push(key);
    }
    keys.reverse();

    // Rather than returning an object with a next method, we keep
    // things simple and return the next function itself.
    return function next() {
      while (keys.length) {
        var key = keys.pop();
        if (key in object) {
          next.value = key;
          next.done = false;
          return next;
        }
      }

      // To avoid creating an additional object, we just hang the .value
      // and .done properties off the next function object itself. This
      // also ensures that the minifier will not anonymize the function.
      next.done = true;
      return next;
    };
  };

  function values(iterable) {
    if (iterable) {
      var iteratorMethod = iterable[iteratorSymbol];
      if (iteratorMethod) {
        return iteratorMethod.call(iterable);
      }

      if (typeof iterable.next === "function") {
        return iterable;
      }

      if (!isNaN(iterable.length)) {
        var i = -1, next = function next() {
          while (++i < iterable.length) {
            if (hasOwn.call(iterable, i)) {
              next.value = iterable[i];
              next.done = false;
              return next;
            }
          }

          next.value = undefined$1;
          next.done = true;

          return next;
        };

        return next.next = next;
      }
    }

    // Return an iterator with no values.
    return { next: doneResult };
  }
  exports.values = values;

  function doneResult() {
    return { value: undefined$1, done: true };
  }

  Context.prototype = {
    constructor: Context,

    reset: function(skipTempReset) {
      this.prev = 0;
      this.next = 0;
      // Resetting context._sent for legacy support of Babel's
      // function.sent implementation.
      this.sent = this._sent = undefined$1;
      this.done = false;
      this.delegate = null;

      this.method = "next";
      this.arg = undefined$1;

      this.tryEntries.forEach(resetTryEntry);

      if (!skipTempReset) {
        for (var name in this) {
          // Not sure about the optimal order of these conditions:
          if (name.charAt(0) === "t" &&
              hasOwn.call(this, name) &&
              !isNaN(+name.slice(1))) {
            this[name] = undefined$1;
          }
        }
      }
    },

    stop: function() {
      this.done = true;

      var rootEntry = this.tryEntries[0];
      var rootRecord = rootEntry.completion;
      if (rootRecord.type === "throw") {
        throw rootRecord.arg;
      }

      return this.rval;
    },

    dispatchException: function(exception) {
      if (this.done) {
        throw exception;
      }

      var context = this;
      function handle(loc, caught) {
        record.type = "throw";
        record.arg = exception;
        context.next = loc;

        if (caught) {
          // If the dispatched exception was caught by a catch block,
          // then let that catch block handle the exception normally.
          context.method = "next";
          context.arg = undefined$1;
        }

        return !! caught;
      }

      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        var record = entry.completion;

        if (entry.tryLoc === "root") {
          // Exception thrown outside of any try block that could handle
          // it, so set the completion value of the entire function to
          // throw the exception.
          return handle("end");
        }

        if (entry.tryLoc <= this.prev) {
          var hasCatch = hasOwn.call(entry, "catchLoc");
          var hasFinally = hasOwn.call(entry, "finallyLoc");

          if (hasCatch && hasFinally) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            } else if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }

          } else if (hasCatch) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            }

          } else if (hasFinally) {
            if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }

          } else {
            throw new Error("try statement without catch or finally");
          }
        }
      }
    },

    abrupt: function(type, arg) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc <= this.prev &&
            hasOwn.call(entry, "finallyLoc") &&
            this.prev < entry.finallyLoc) {
          var finallyEntry = entry;
          break;
        }
      }

      if (finallyEntry &&
          (type === "break" ||
           type === "continue") &&
          finallyEntry.tryLoc <= arg &&
          arg <= finallyEntry.finallyLoc) {
        // Ignore the finally entry if control is not jumping to a
        // location outside the try/catch block.
        finallyEntry = null;
      }

      var record = finallyEntry ? finallyEntry.completion : {};
      record.type = type;
      record.arg = arg;

      if (finallyEntry) {
        this.method = "next";
        this.next = finallyEntry.finallyLoc;
        return ContinueSentinel;
      }

      return this.complete(record);
    },

    complete: function(record, afterLoc) {
      if (record.type === "throw") {
        throw record.arg;
      }

      if (record.type === "break" ||
          record.type === "continue") {
        this.next = record.arg;
      } else if (record.type === "return") {
        this.rval = this.arg = record.arg;
        this.method = "return";
        this.next = "end";
      } else if (record.type === "normal" && afterLoc) {
        this.next = afterLoc;
      }

      return ContinueSentinel;
    },

    finish: function(finallyLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.finallyLoc === finallyLoc) {
          this.complete(entry.completion, entry.afterLoc);
          resetTryEntry(entry);
          return ContinueSentinel;
        }
      }
    },

    "catch": function(tryLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc === tryLoc) {
          var record = entry.completion;
          if (record.type === "throw") {
            var thrown = record.arg;
            resetTryEntry(entry);
          }
          return thrown;
        }
      }

      // The context.catch method must only be called with a location
      // argument that corresponds to a known catch block.
      throw new Error("illegal catch attempt");
    },

    delegateYield: function(iterable, resultName, nextLoc) {
      this.delegate = {
        iterator: values(iterable),
        resultName: resultName,
        nextLoc: nextLoc
      };

      if (this.method === "next") {
        // Deliberately forget the last sent value so that we don't
        // accidentally pass it on to the delegate.
        this.arg = undefined$1;
      }

      return ContinueSentinel;
    }
  };

  // Regardless of whether this script is executing as a CommonJS module
  // or not, return the runtime object so that we can declare the variable
  // regeneratorRuntime in the outer scope, which allows this module to be
  // injected easily by `bin/regenerator --include-runtime script.js`.
  return exports;

}(
  // If this script is executing as a CommonJS module, use module.exports
  // as the regeneratorRuntime namespace. Otherwise create a new empty
  // object. Either way, the resulting object will be used to initialize
  // the regeneratorRuntime variable at the top of this file.
  module.exports 
));

try {
  regeneratorRuntime = runtime;
} catch (accidentalStrictMode) {
  // This module should not be running in strict mode, so the above
  // assignment should always work unless something is misconfigured. Just
  // in case runtime.js accidentally runs in strict mode, in modern engines
  // we can explicitly access globalThis. In older engines we can escape
  // strict mode using a global Function call. This could conceivably fail
  // if a Content Security Policy forbids using Function, but in that case
  // the proper solution is to fix the accidental strict mode problem. If
  // you've misconfigured your bundler to force strict mode and applied a
  // CSP to forbid Function, and you're not willing to fix either of those
  // problems, please detail your unique predicament in a GitHub issue.
  if (typeof globalThis === "object") {
    globalThis.regeneratorRuntime = runtime;
  } else {
    Function("r", "regeneratorRuntime = r")(runtime);
  }
}
}(runtime));

var regenerator = runtime.exports;

/**
 * useMyAlgoConnect
 * @param {Function} onConnect On Connect Callback
 * @param {Function} onDisconnect On Disconnect Callback
 * @return {WalletEffect}
 */

function useMyAlgoConnect(onConnect, onDisconnect) {
  // Instance reference
  var myAlgoWallet = React.useRef();

  var disconnect = function disconnect() {
    onDisconnect();
  };

  var connect = /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/regenerator.mark(function _callee() {
      var accounts, _addresses;

      return regenerator.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.prev = 0;

              if (myAlgoWallet.current) {
                _context.next = 4;
                break;
              }

              console.error(ERROR.FAILED_TO_INIT);
              return _context.abrupt("return");

            case 4:
              _context.next = 6;
              return myAlgoWallet.current.connect();

            case 6:
              accounts = _context.sent;
              // Map the connector to the address list
              _addresses = accounts.map(function (acct) {
                acct.type = 'my-algo-wallet';
                acct.connector = myAlgoWallet.current;
                acct.connector.connected = true;
                return acct;
              });
              console.debug('Setting Address form myAlgoConnect', _addresses); // Set Addresses

              onConnect(_addresses);
              _context.next = 15;
              break;

            case 12:
              _context.prev = 12;
              _context.t0 = _context["catch"](0);
              console.error(ERROR.FAILED_TO_CONNECT, _context.t0);

            case 15:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[0, 12]]);
    }));

    return function connect() {
      return _ref.apply(this, arguments);
    };
  }();

  React.useEffect(function () {
    var initMyAlgoWallet = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/regenerator.mark(function _callee2() {
        var MyAlgoConnect;
        return regenerator.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return Promise.resolve().then(function () { return /*#__PURE__*/_interopNamespace(require('@randlabs/myalgo-connect')); });

              case 2:
                MyAlgoConnect = _context2.sent.default;
                MyAlgoConnect.prototype.sign = signer__default["default"];
                myAlgoWallet.current = new MyAlgoConnect();
                myAlgoWallet.current.connected = false;

              case 6:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }));

      return function initMyAlgoWallet() {
        return _ref2.apply(this, arguments);
      };
    }();

    initMyAlgoWallet();
  }, []);
  return {
    connect: connect,
    disconnect: disconnect,
    connector: myAlgoWallet.current
  };
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

var ERROR$1 = {
  FAILED_TO_INIT: 'MyAlgo Wallet failed to initialize.',
  FAILED_TO_CONNECT: 'MyAlgo Wallet failed to connect.'
};
/**
 * Use Wallet Connect query
 * @param {Function} onConnect On Connect Callback
 * @param {Function} onDisconnect On Disconnect Callback
 * @return {object}
 */

function useWalletConnect(onConnect, onDisconnect) {
  var _useState = React.useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      addresses = _useState2[0],
      setAddresses = _useState2[1];
  /**
   * Instance referenc
   */


  var walletConnect = React.useRef();

  var connect = /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/regenerator.mark(function _callee() {
      var _addresses;

      return regenerator.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.prev = 0;

              if (walletConnect.current) {
                _context.next = 4;
                break;
              }

              console.error(ERROR$1.FAILED_TO_INIT);
              return _context.abrupt("return");

            case 4:
              // Check if connection is already established
              if (!walletConnect.current.connected) {
                // create new session
                walletConnect.current.createSession();
              } else {
                QRCodeModal__default["default"].close();
              } // Map the connector to the address list


              _addresses = walletConnect.current.accounts.map(function (acct) {
                return {
                  name: 'WalletConnect',
                  address: acct,
                  type: 'wallet-connect',
                  connector: walletConnect.current
                };
              });
              setAddresses(_addresses);
              _context.next = 12;
              break;

            case 9:
              _context.prev = 9;
              _context.t0 = _context["catch"](0);
              console.error(ERROR$1.FAILED_TO_CONNECT, _context.t0);

            case 12:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[0, 9]]);
    }));

    return function connect() {
      return _ref.apply(this, arguments);
    };
  }();

  var disconnect = function disconnect() {
    if (walletConnect.current.connected) {
      walletConnect.current.killSession();
    }
  };

  React.useEffect(function () {
    var initWalletConnect = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/regenerator.mark(function _callee2() {
        var WalletConnect;
        return regenerator.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return Promise.resolve().then(function () { return /*#__PURE__*/_interopNamespace(require('@walletconnect/client')); });

              case 2:
                WalletConnect = _context2.sent.default;
                _context2.next = 5;
                return Promise.resolve().then(function () { return /*#__PURE__*/_interopNamespace(require('@algodex/algodex-sdk/lib/wallet/signers/WalletConnect')); });

              case 5:
                WalletConnect.prototype.sign = _context2.sent.default;
                walletConnect.current = new WalletConnect({
                  bridge: 'https://bridge.walletconnect.org',
                  // Required
                  qrcodeModal: QRCodeModal__default["default"]
                });
                walletConnect.current.connected = false;

              case 8:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }));

      return function initWalletConnect() {
        return _ref2.apply(this, arguments);
      };
    }();

    initWalletConnect();
  }, []);
  var handleDisconnect = React.useCallback(function (err) {
    console.log('DISCONNECTED');
    if (err) throw err;
    onDisconnect(addresses);
  }, [onDisconnect]);

  var handleConnected = function handleConnected(err, payload) {
    console.log('CONNECTED');

    if (err) {
      throw err;
    }

    var accounts = []; // Get provided accounts

    if (typeof payload !== 'undefined' && Array.isArray(payload.params)) {
      accounts = payload.params[0].accounts;
    } // Map the connector to the address list


    var _addresses = accounts.map(function (acct) {
      return {
        type: 'wallet-connect',
        connector: walletConnect.current,
        address: acct
      };
    });

    console.log('connected here');
    onConnect(_addresses);
    QRCodeModal__default["default"].close();
  };

  React.useEffect(function () {
    // let listener;
    if (typeof walletConnect.current !== 'undefined') {
      walletConnect.current.on('connect', handleConnected);
      walletConnect.current.on('session_update', handleConnected);
      walletConnect.current.on('disconnect', handleDisconnect);
    }

    return function () {
      if (typeof walletConnect.current !== 'undefined') {
        walletConnect.current.off('connect');
        walletConnect.current.off('session_update');
        walletConnect.current.off('disconnect');
      }
    };
  }, [walletConnect.current]);
  return {
    connect: connect,
    disconnect: disconnect,
    connector: walletConnect.current
  };
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys$a(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$a(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$a(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$a(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
/**
 *
 * @param {Array<Wallet>} a
 * @param {Array<Wallet>} b
 * @return {Array<Wallet>}
 * @private
 */

function _mergeAddresses(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) {
    throw new TypeError('Must be an array of addresses!');
  }

  var map = new Map();
  a.forEach(function (wallet) {
    return map.set(wallet.address, wallet);
  });
  b.forEach(function (wallet) {
    return map.set(wallet.address, _objectSpread$a(_objectSpread$a({}, map.get(wallet.address)), wallet));
  });
  return Array.from(map.values());
}
/**
 * Use Wallets Hooks
 * @param {Object} initialState Wallet Initial State
 * @return {*}
 */


function useWallets(initialState) {
  var _useState = React.useState(initialState),
      _useState2 = _slicedToArray(_useState, 2),
      wallet = _useState2[0],
      setWallet = _useState2[1];

  var _useState3 = React.useState(),
      _useState4 = _slicedToArray(_useState3, 2),
      activeWallet = _useState4[0],
      setActiveWallet = _useState4[1];

  var _useState5 = React.useState([]),
      _useState6 = _slicedToArray(_useState5, 2),
      addresses = _useState6[0],
      setAddresses = _useState6[1];

  var _useAlgodex = useAlgodex(),
      http = _useAlgodex.http;

  var onEvents = React.useCallback(function (props) {
    var type = props.type,
        _wallet = props.wallet;

    if (type === 'change' && !lang.isEqual(wallet, _wallet)) {
      setWallet(_wallet);
      setActiveWallet(_wallet.address);
    }
  }, [setWallet, wallet]);
  React.useEffect(function () {
    events__default["default"].on('wallet', onEvents);
    return function () {
      events__default["default"].off('wallet', onEvents);
    };
  }, [onEvents]); // TODO: Account Info Query
  // Handle any Connection

  var handleConnect = React.useCallback( /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/regenerator.mark(function _callee(_addresses) {
      var accounts;
      return regenerator.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return http.indexer.fetchAccounts(_addresses);

            case 2:
              accounts = _context.sent;
              console.log(accounts);
              setAddresses(_mergeAddresses(_addresses, accounts));

            case 5:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function (_x) {
      return _ref.apply(this, arguments);
    };
  }(), [setAddresses]); // Handle any Disconnect

  var handleDisconnect = React.useCallback(function (_addresses) {
    console.error('Handle removing from storage', _addresses);
  }, []); // My Algo Connect/Disconnect

  var _useMyAlgoConnect = useMyAlgoConnect(handleConnect, handleDisconnect),
      myAlgoConnect = _useMyAlgoConnect.connect,
      myAlgoDisconnect = _useMyAlgoConnect.disconnect; // Pera Connect/Disconnect


  var _useWalletConnect = useWalletConnect(handleConnect, handleDisconnect),
      peraConnect = _useWalletConnect.connect,
      peraDisconnect = _useWalletConnect.disconnect; // Fetch active wallet from local storage


  React.useEffect(function () {
    var res = localStorage.getItem('activeWallet');

    if (res && res !== activeWallet) {
      setActiveWallet(localStorage.getItem('activeWallet'));
    }
  }, [setActiveWallet]); // Fetch all wallet addresses from local storage

  React.useEffect(function () {
    var res = localStorage.getItem('addresses');

    if (res) {
      setAddresses(JSON.parse(localStorage.getItem('addresses')));
    }
  }, []);
  React.useEffect(function () {
    localStorage.setItem('addresses', JSON.stringify(addresses));
  }, [addresses]);
  return {
    wallet: wallet,
    setWallet: setWallet,
    addresses: addresses,
    myAlgoConnect: myAlgoConnect,
    peraConnect: peraConnect,
    peraDisconnect: peraDisconnect,
    myAlgoDisconnect: myAlgoDisconnect
  };
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

var _templateObject$1, _templateObject2$1;
var FlexContainer$1 = styled__default["default"].div(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n  flex: 1 1 0%;\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  justify-content: center;\n"])));
var Message = styled__default["default"].p(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteral(["\n  display: flex;\n  align-items: center;\n  font-size: 0.875rem;\n  font-weight: 600;\n  margin: ", ";\n  color: ", ";\n"])), function (_ref) {
  var flex = _ref.flex;
  return flex ? '0.375rem 0' : '0 0 1rem 0';
}, function (_ref2) {
  var color = _ref2.color;
  return "".concat(color);
});
/**
 * Error Message
 *
 * @param {object} props Component Properties
 * @param {number} props.size <AlertIcon> Size
 * @param {string} props.color Component Color
 * @param {boolean} props.flex Enable Flex
 * @param {string} props.message Display Message
 * @param {JSXElement} props.Icon Icon Component to Render
 * @return {JSX.Element}
 * @constructor
 */

function ServiceError(_ref3) {
  var size = _ref3.size,
      color = _ref3.color,
      flex = _ref3.flex,
      message = _ref3.message,
      Icon = _ref3.Icon;
  var showMsg = (message === null || message === void 0 ? void 0 : message.length) > 0;

  if (flex) {
    return /*#__PURE__*/React__default["default"].createElement(FlexContainer$1, {
      "data-testid": "flex-service"
    }, /*#__PURE__*/React__default["default"].createElement(Icon, {
      size: size,
      color: color
    }), showMsg && /*#__PURE__*/React__default["default"].createElement(Message, {
      "data-testid": "msg-flex",
      color: color,
      flex: flex
    }, message));
  } else {
    return /*#__PURE__*/React__default["default"].createElement(Message, {
      "data-testid": "mssg-service",
      color: color,
      flex: flex
    }, /*#__PURE__*/React__default["default"].createElement(Icon, {
      size: size,
      color: color
    }), message);
  }
}
ServiceError.propTypes = {
  size: PropTypes__default["default"].number,
  color: PropTypes__default["default"].string,
  message: PropTypes__default["default"].string,
  flex: PropTypes__default["default"].bool,
  Icon: PropTypes__default["default"].element
};
ServiceError.defaultProps = {
  size: 1.5,
  color: 'gray.600',
  flex: false,
  message: 'Something went wrong!',
  Icon: function Icon(props) {
    return /*#__PURE__*/React__default["default"].createElement("div", props, "Missing Icon!");
  }
};

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var _excluded$9 = ["size", "color", "flex"];

var _templateObject, _templateObject2, _templateObject3, _templateObject4;
var rotate = react.keyframes(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  100% {\n    transform: rotate(360deg);\n  }\n"])));
var dash = react.keyframes(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  0% {\n    stroke-dasharray: 1, 150;\n    stroke-dashoffset: 0;\n  }\n  50% {\n    stroke-dasharray: 90, 150;\n    stroke-dashoffset: -35;\n  }\n  100% {\n    stroke-dasharray: 90, 150;\n    stroke-dashoffset: -124;\n  }\n"])));
/**
 * @todo Move to components/Layout.jsx
 * @type {StyledComponent}
 */

var FlexContainer = styled__default["default"].div(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  flex: 1 1 0%;\n  height: 100%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n"])));
/**
 * Spinner SVG
 * @return {StyledComponent}
 */

var Svg = styled__default["default"].svg(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  animation: ", " 2s linear infinite;\n  width: ", ";\n  height: ", ";\n  opacity: 0.75;\n  will-change: transform;\n\n  circle {\n    stroke: gray;\n    stroke-width: 4;\n    stroke-linecap: round;\n    fill: none;\n    animation: ", " 1300ms ease-in-out infinite;\n    will-change: stroke-dasharray, stroke-dashoffset;\n  }\n"])), rotate, function (_ref) {
  var size = _ref.size;
  return "".concat(size, "rem");
}, function (_ref2) {
  var size = _ref2.size;
  return "".concat(size, "rem");
}, dash);
Svg.defaultProps = {
  viewBox: '0 0 50 50'
};
/**
 * Loading Spinner
 *
 * Used to show a loading screen for asynchronous operations
 *
 * @param {object} props Component Properties
 * @param {number} props.size SVG Size
 * @param {string} props.color SVG Color
 * @param {boolean} props.flex Enable Flex
 * @return {JSX.Element}
 *
 * @todo Refactor to Tailwinds class="spinner"
 * @constructor
 */

function Spinner(_ref3) {
  var size = _ref3.size,
      color = _ref3.color,
      flex = _ref3.flex,
      rest = _objectWithoutProperties(_ref3, _excluded$9);

  if (flex) {
    return /*#__PURE__*/React__default["default"].createElement(FlexContainer, {
      "data-testid": "spinner-flex-container"
    }, /*#__PURE__*/React__default["default"].createElement(Svg, _extends({
      size: size,
      color: color
    }, rest, {
      "data-testid": "spinner-svg"
    }), /*#__PURE__*/React__default["default"].createElement("circle", {
      cx: "25",
      cy: "25",
      r: "20"
    })));
  } else {
    return /*#__PURE__*/React__default["default"].createElement(Svg, _extends({
      size: size,
      color: color
    }, rest, {
      "data-testid": "spinner-svg"
    }), /*#__PURE__*/React__default["default"].createElement("circle", {
      cx: "25",
      cy: "25",
      r: "20"
    }));
  }
}
Spinner.propTypes = {
  size: PropTypes__default["default"].number,
  color: PropTypes__default["default"].string,
  flex: PropTypes__default["default"].bool
};
Spinner.defaultProps = {
  size: 5,
  color: 'gray.800',
  flex: true
};

/**
 * Base withQuery Abstraction
 *
 * Return an element based on Query State
 *
 * @param {JSX.Element} Component Component to wrap
 * @param {object} options Query Options
 * @param {function} options.hook Callable Hook
 * @param {object} options.components Hook Components
 * @return {JSX.Element} Return a composed component
 */

function withQuery(Component, _ref) {
  var _ref$hook = _ref.hook,
      hook = _ref$hook === void 0 ? reactQuery.useQuery : _ref$hook,
      components = _ref.components;

  if (lang.isUndefined(components) || lang.isUndefined(components.Loading) || lang.isUndefined(components.ServiceError)) {
    throw new Error('Must have Loading and Error components');
  }

  var Loading = components.Loading,
      ServiceError = components.ServiceError;
  /**
   * Wrapped Component
   * @param {object} props
   * @return {JSX.Element}
   */

  function withQueryWrapper(props) {
    var _hook = hook(props),
        isSuccess = _hook.isSuccess,
        isLoading = _hook.isLoading,
        isError = _hook.isError,
        data = _hook.data,
        error = _hook.error;

    var successMemo = React.useMemo(function () {
      return /*#__PURE__*/React__default["default"].createElement(Component, _extends({}, props, data));
    }, [props, data]);
    var loadingMemo = React.useMemo(function () {
      return /*#__PURE__*/React__default["default"].createElement(Loading, _extends({}, props, data));
    }, [props, data]);
    var errorMemo = React.useMemo(function () {
      return /*#__PURE__*/React__default["default"].createElement(ServiceError, _extends({}, data, {
        message: error === null || error === void 0 ? void 0 : error.message
      }));
    }, [error, data]);
    if (isSuccess) return successMemo;
    if (isLoading) return loadingMemo;
    if (isError) return errorMemo;
  }

  withQueryWrapper.getInitialProps = Component.getInitialProps;
  return withQueryWrapper;
}

var _excluded$8 = ["data", "isError", "error"];

function ownKeys$9(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$9(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$9(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$9(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var DEBUG = process.env.NEXT_PUBLIC_DEBUG || process.env.DEBUG || false;
var refetchInterval$7 = 3000;
var components$9 = {
  Loading: Spinner,
  ServiceError: ServiceError
};
/**
 *
 * @param {object} asset
 * @param {object} options
 * @return {object}
 */

function useExplorerAssetInfo(_ref) {
  var asset = _ref.asset,
      options = _ref.options;
  DEBUG && console.debug("useExplorerAssetInfo"); // const router = useRouter();

  var _useAlgodex = useAlgodex(),
      http = _useAlgodex.http;

  var id = asset.id;

  var _useQuery = reactQuery.useQuery(['explorerAsset', id], function () {
    return http.explorer.fetchExplorerAssetInfo(id);
  }, options),
      data = _useQuery.data,
      isError = _useQuery.isError,
      error = _useQuery.error,
      rest = _objectWithoutProperties(_useQuery, _excluded$8); // console.log(data)
  // useEffect(() => {
  //   let mounted = true
  //   if (mounted) {
  //     routeQueryError({ isError, error, router })
  //   }
  //   return () => (mounted = false)
  // }, [router, data, isError, error])


  return _objectSpread$9({
    data: data,
    isError: isError,
    error: error
  }, rest);
}
/**
 *
 * @param {JSX.Element} Component
 * @param {object} [options]
 * @return {*}
 */

function withExplorerAssetInfo(Component, options) {
  return withQuery(Component, _objectSpread$9({
    hook: useExplorerAssetInfo,
    components: components$9
  }, options));
}
/**
 * Use Search Results Query
 * @param {Object} props The props of the parent
 * @param {string} props.query Search Query
 * @param {Object} [props.options] useQuery Options
 * @return {UseQueryResult<{assets: *}, unknown>}
 */

function useAlgorandPriceQuery() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref2$query = _ref2.query,
      query = _ref2$query === void 0 ? '' : _ref2$query,
      _ref2$options = _ref2.options,
      options = _ref2$options === void 0 ? {
    refetchInterval: query === '' ? refetchInterval$7 : 20000
  } : _ref2$options;

  var _useAlgodex2 = useAlgodex(),
      http = _useAlgodex2.http;

  return reactQuery.useQuery(['fetchAlgorandPrice', {
    query: query
  }], function () {
    return http.explorer.fetchAlgorandPrice(query);
  }, options);
}
/**
 * With Algorand Price Query
 * @param {JSX.Element| Function} Component Component to wrap
 * @param {object} [options] Options to pass to withQuery
 * @return {JSX.Element}
 */

function withAlgorandPriceQuery(Component, options) {
  return withQuery(Component, _objectSpread$9({
    hook: useAlgorandPriceQuery,
    components: components$9
  }, options));
}

function ownKeys$8(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$8(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$8(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$8(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var components$8 = {
  Loading: Spinner,
  ServiceError: ServiceError
};
/**
 *
 * @todo add to PlaceOrder
 * @param {JSX.Element| Function} Component Component to wrap
 * @param {Object} [options]
 * @return {JSX.Element}
 */

function withAssetOrdersQuery(Component, options) {
  return withQuery(Component, _objectSpread$8({
    hook: useAssetOrdersQuery,
    components: components$8
  }, options));
}
/**
 *
 * @param {object} asset
 * @param {object} options
 * @return {UseQueryResult<Object, unknown>}
 */

function useAssetOrdersQuery(_ref) {
  var asset = _ref.asset,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? {} : _ref$options;

  var _useAlgodex = useAlgodex(),
      http = _useAlgodex.http;

  var id = asset.id;
  return reactQuery.useQuery(['assetOrders', {
    id: id
  }], function () {
    return http.dexd.fetchAssetOrders(id);
  }, options);
}

var _excluded$7 = ["isLoading", "isError", "data", "isSuccess"];

function ownKeys$7(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$7(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$7(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$7(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var refetchInterval$6 = 3000;
var components$7 = {
  Loading: Spinner,
  ServiceError: ServiceError
};
/**
 *
 * @param {JSX.Element} Component
 * @param {object} [options]
 * @return {JSX.Element}
 */

function withAssetChartQuery(Component, options) {
  return withQuery(Component, _objectSpread$7({
    hook: useAssetChartQuery,
    components: components$7
  }, options));
}
/**
 *
 * @param {Object} a
 * @param {Object} b
 * @return {number}
 */

function sortPriceByTime(a, b) {
  return a.time < b.time ? -1 : a.time > b.time ? 1 : 0;
}
/**
 *
 * @param {object} data
 * @return {array}
 */

function mapPriceData(data) {
  var prices = (data === null || data === void 0 ? void 0 : data.chart_data.map(function (_ref) {
    var formatted_open = _ref.formatted_open,
        formatted_high = _ref.formatted_high,
        formatted_low = _ref.formatted_low,
        formatted_close = _ref.formatted_close,
        unixTime = _ref.unixTime;
    var time = parseInt(unixTime);
    return {
      time: time,
      open: floatToFixed__default["default"](formatted_open),
      high: floatToFixed__default["default"](formatted_high),
      low: floatToFixed__default["default"](formatted_low),
      close: floatToFixed__default["default"](formatted_close)
    };
  })) || [];
  return prices.sort(sortPriceByTime);
}
/**
 * {high: String, low: String, close: String, open: String}
 * @param {object} data
 * @return {object}
 */

function getOhlc(data) {
  var lastPriceData = data === null || data === void 0 ? void 0 : data.chart_data[0];
  return lastPriceData ? {
    open: floatToFixed__default["default"](lastPriceData.formatted_open),
    high: floatToFixed__default["default"](lastPriceData.formatted_high),
    low: floatToFixed__default["default"](lastPriceData.formatted_low),
    close: floatToFixed__default["default"](lastPriceData.formatted_close)
  } : {};
}
/**
 *
 * @param {object} data
 * @param {string} volUpColor
 * @param {string} volDownColor
 * @return {array}
 */

function mapVolumeData(data, volUpColor, volDownColor) {
  var _data$chart_data;

  var mappedData = data === null || data === void 0 ? void 0 : (_data$chart_data = data.chart_data) === null || _data$chart_data === void 0 ? void 0 : _data$chart_data.map(function (_ref2) {
    var asaVolume = _ref2.asaVolume,
        unixTime = _ref2.unixTime;
    var time = parseInt(unixTime);
    return {
      time: time,
      value: asaVolume
    };
  });
  var volumeColors = data === null || data === void 0 ? void 0 : data.chart_data.map(function (_ref3) {
    var open = _ref3.open,
        close = _ref3.close;
    return open > close ? volDownColor : volUpColor;
  });
  return (mappedData === null || mappedData === void 0 ? void 0 : mappedData.map(function (md, i) {
    return _objectSpread$7(_objectSpread$7({}, md), {}, {
      color: volumeColors[i]
    });
  })) || [];
}
/**
 *
 * @param {Object} a
 * @param {Object} b
 * @return {number}
 */

function sortByASAPrice(a, b) {
  return b.asaPrice - a.asaPrice;
}
/**
 * {ask: String, bid: String, spread: String}
 * @todo: Move to SDK
 * @param {object} orderBook
 * @return {object}
 */

function getBidAskSpread(orderBook) {
  var _buyOrders$sort, _buyOrders$sort$, _sellOrders$sort, _sellOrders$sort$;

  var buyOrders = orderBook.buyOrders,
      sellOrders = orderBook.sellOrders;
  var bidPrice = ((_buyOrders$sort = buyOrders.sort(function (a, b) {
    return b.asaPrice - a.asaPrice;
  })) === null || _buyOrders$sort === void 0 ? void 0 : (_buyOrders$sort$ = _buyOrders$sort[0]) === null || _buyOrders$sort$ === void 0 ? void 0 : _buyOrders$sort$.formattedPrice) || 0;
  var askPrice = ((_sellOrders$sort = sellOrders.sort(function (a, b) {
    return a.asaPrice - b.asaPrice;
  })) === null || _sellOrders$sort === void 0 ? void 0 : (_sellOrders$sort$ = _sellOrders$sort[0]) === null || _sellOrders$sort$ === void 0 ? void 0 : _sellOrders$sort$.formattedPrice) || 0;
  var bid = floatToFixed__default["default"](bidPrice);
  var ask = floatToFixed__default["default"](askPrice);
  var spread = floatToFixed__default["default"](new Big__default["default"](ask).minus(bid).abs());
  return {
    bid: bid,
    ask: ask,
    spread: spread
  };
}
/**
 * Use Asset Chart Query
 * @param {Object} props The props of the parent
 * @param {Object} props.asset An instance of an Asset
 * @param {string} props.interval Interval to aggregate chart by
 * @param {Object} [props.options] useQuery Options
 * @return {object}
 */

function useAssetChartQuery(_ref4) {
  var _data$chart_data2;

  var interval = _ref4.interval,
      asset = _ref4.asset,
      _ref4$options = _ref4.options,
      options = _ref4$options === void 0 ? {
    refetchInterval: refetchInterval$6
  } : _ref4$options;

  var _useAlgodex = useAlgodex(),
      http = _useAlgodex.http; // console.log(`useAssetChartQuery(${JSON.stringify({ interval, asset })})`)


  var id = asset.id;

  var _useAssetOrdersQuery = useAssetOrdersQuery({
    asset: asset
  }),
      assetOrders = _useAssetOrdersQuery.data,
      isOrdersLoading = _useAssetOrdersQuery.isLoading,
      isOrdersError = _useAssetOrdersQuery.isError,
      isOrdersSuccess = _useAssetOrdersQuery.isSuccess;

  var VOLUME_UP_COLOR = '#2fb16c2c';
  var VOLUME_DOWN_COLOR = '#e53e3e2c';
  var orderBook = React.useMemo(function () {
    return {
      buyOrders: (assetOrders === null || assetOrders === void 0 ? void 0 : assetOrders.buyASAOrdersInEscrow) || [],
      sellOrders: (assetOrders === null || assetOrders === void 0 ? void 0 : assetOrders.sellASAOrdersInEscrow) || []
    };
  }, [assetOrders]);

  var _useMemo = React.useMemo(function () {
    return getBidAskSpread(orderBook);
  }, [orderBook]),
      bid = _useMemo.bid,
      ask = _useMemo.ask,
      spread = _useMemo.spread;

  var _useQuery = reactQuery.useQuery(['assetChart', {
    id: id,
    interval: interval
  }], function () {
    return http.dexd.fetchAssetChart(id, interval);
  }, options),
      isChartLoading = _useQuery.isLoading,
      isChartError = _useQuery.isError,
      data = _useQuery.data,
      isChartSuccess = _useQuery.isSuccess,
      rest = _objectWithoutProperties(_useQuery, _excluded$7);

  var priceData = React.useMemo(function () {
    return mapPriceData(data);
  }, [data]);
  var volumeData = React.useMemo(function () {
    return mapVolumeData(data, VOLUME_UP_COLOR, VOLUME_DOWN_COLOR);
  }, [data]);
  var ohlcOverlay = React.useMemo(function () {
    return getOhlc(data);
  }, [data]);
  var volume = millify__default["default"]((data === null || data === void 0 ? void 0 : (_data$chart_data2 = data.chart_data[(data === null || data === void 0 ? void 0 : data.chart_data.length) - 1]) === null || _data$chart_data2 === void 0 ? void 0 : _data$chart_data2.asaVolume) || 0);
  var isLoading = isOrdersLoading || isChartLoading;
  var isError = isOrdersError || isChartError;
  var isSuccess = isOrdersSuccess && isChartSuccess;
  return _objectSpread$7({
    data: {
      overlay: {
        ohlc: ohlcOverlay,
        orderbook: {
          bid: bid,
          ask: ask,
          spread: spread
        },
        volume: volume
      },
      volume: volumeData,
      ohlc: priceData,
      isLoading: isLoading,
      isError: isError
    },
    isLoading: isLoading,
    isError: isError,
    isSuccess: isSuccess
  }, rest);
}

var _excluded$6 = ["data", "isLoading"];

function ownKeys$6(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$6(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$6(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$6(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var refetchInterval$5 = 3000;
var components$6 = {
  Loading: Spinner,
  ServiceError: ServiceError
};
/**
 *
 * @param {JSX.Element} Component
 * @param {object} [options]
 * @return {JSX.Element}
 */

function withAssetOrderbookQuery(Component, options) {
  return withQuery(Component, _objectSpread$6({
    hook: useAssetOrderbookQuery,
    components: components$6
  }, options));
}
/**
 * Use Asset Orders Query
 * @param {Object} props The props of the parent
 * @param {Object} props.asset An instance of an Asset
 * @param {Object} [props.options] useQuery Options
 * @return {object} React Query Results
 */

function useAssetOrderbookQuery() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      asset = _ref.asset,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? {
    refetchInterval: refetchInterval$5,
    notifyOnChangeProps: ['data', 'error']
  } : _ref$options;

  var _useAlgodex = useAlgodex(),
      http = _useAlgodex.http;

  var id = asset.id,
      decimals = asset.decimals;

  if (typeof id === 'undefined') {
    throw new TypeError('Must have valid id!');
  }

  if (typeof decimals === 'undefined') {
    throw new TypeError('Must have valid decimals!');
  } // Orderbook Query


  var _useQuery = reactQuery.useQuery(['assetOrders', {
    id: id
  }], function () {
    return http.dexd.fetchAssetOrders(id);
  }, options),
      data = _useQuery.data,
      isLoading = _useQuery.isLoading,
      rest = _objectWithoutProperties(_useQuery, _excluded$6);

  var _useState = React.useState(''),
      _useState2 = _slicedToArray(_useState, 2),
      etag = _useState2[0],
      setEtag = _useState2[1];

  React.useMemo(function () {
    if ((data === null || data === void 0 ? void 0 : data.etag) !== etag) {
      setEtag((data === null || data === void 0 ? void 0 : data.etag) || '');
    }
  }, [data, etag]);
  var retdata = React.useMemo(function () {
    if (data && !isLoading && typeof data.sellASAOrdersInEscrow !== 'undefined' && typeof data.buyASAOrdersInEscrow !== 'undefined') {
      var sell = http.dexd.aggregateOrders(data.sellASAOrdersInEscrow, decimals, 'sell');
      var buy = http.dexd.aggregateOrders(data.buyASAOrdersInEscrow, decimals, 'buy');
      return _objectSpread$6({
        data: {
          orders: {
            sell: sell,
            buy: buy
          },
          isLoading: isLoading
        },
        isLoading: isLoading
      }, rest);
    }

    return _objectSpread$6({
      data: {
        orders: {
          sell: [],
          buy: []
        },
        isLoading: isLoading
      },
      isLoading: isLoading
    }, rest);
  }, [etag]);
  return retdata; // Return OrderBook
}

var _excluded$5 = ["data"];

function ownKeys$5(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$5(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$5(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$5(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var refetchInterval$4 = 3000;
var components$5 = {
  Loading: Spinner,
  ServiceError: ServiceError
};
/**
 * With Asset Price Query
 * @param {JSX.Element| Function} Component Component to wrap
 * @param {object} [options] withQuery Options
 * @return {JSX.Element}
 */

function withAssetPriceQuery(Component, options) {
  return withQuery(Component, _objectSpread$5({
    hook: useAssetPriceQuery,
    components: components$5
  }, options));
}
/**
 * Use Asset Price Query
 *
 * @param {Object} props The props of the parent
 * @param {Object} props.asset An instance of an Asset
 * @param {Object} [props.options] useQuery Options
 * @todo: Consolidate with Search
 * @return {object} Massaged Query
 */

function useAssetPriceQuery() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      algorandAsset = _ref.asset,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? {
    refetchInterval: refetchInterval$4,
    notifyOnChangeProps: ['data', 'error']
  } : _ref$options;

  var _useAlgodex = useAlgodex(),
      http = _useAlgodex.http;

  var id = algorandAsset.id;

  var _useQuery = reactQuery.useQuery(['assetPrice', {
    id: id
  }], function () {
    return http.dexd.fetchAssetPrice(id);
  }, options),
      dexAsset = _useQuery.data,
      rest = _objectWithoutProperties(_useQuery, _excluded$5);

  var retdata = React.useMemo(function () {
    var asset = _objectSpread$5(_objectSpread$5({}, algorandAsset), {}, {
      price_info: dexAsset
    });

    return _objectSpread$5({
      data: {
        asset: asset
      }
    }, rest);
  }, [algorandAsset, dexAsset]);
  return retdata;
}

var _excluded$4 = ["data"];

function ownKeys$4(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$4(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$4(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$4(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var components$4 = {
  Loading: Spinner,
  ServiceError: ServiceError
};
/**
 *
 * @param {JSX.Element} Component React Component
 * @param {object} [options] Extra options for hooks
 * @return {JSX.Element}
 */

function withAssetTradeHistoryQuery(Component, options) {
  return withQuery(Component, _objectSpread$4({
    hook: useAssetTradeHistoryQuery,
    components: components$4
  }, options));
}
/**
 * Use Asset Trade History Query
 * @param {Object} props The props of the parent
 * @param {Object} props.asset An instance of an Asset
 * @param {Object} [props.options] useQuery Options
 * @return {object} Massaged React-Query
 */

function useAssetTradeHistoryQuery(_ref) {
  var asset = _ref.asset,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? {
    refetchInterval: 5000,
    staleTime: 3000
  } : _ref$options;

  var _useAlgodex = useAlgodex(),
      http = _useAlgodex.http;

  var id = asset.id;

  var _useQuery = reactQuery.useQuery(['assetTradeHistory', {
    id: id
  }], function () {
    return http.dexd.fetchAssetTradeHistory(id);
  }, options),
      data = _useQuery.data,
      rest = _objectWithoutProperties(_useQuery, _excluded$4);

  var tradesData = (data === null || data === void 0 ? void 0 : data.transactions.map(function (txn) {
    return {
      id: txn.PK_trade_history_id,
      type: txn.tradeType,
      price: floatToFixed__default["default"](txn.formattedPrice),
      amount: txn.formattedASAAmount,
      timestamp: txn.unix_time * 1000
    };
  })) || [];
  return _objectSpread$4({
    data: {
      orders: tradesData
    }
  }, rest);
}

var _excluded$3 = ["data", "isError", "error"];

function ownKeys$3(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$3(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$3(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$3(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var refetchInterval$3 = 3000;
var components$3 = {
  Loading: Spinner,
  ServiceError: ServiceError
};
/**
 * With Search Results Query
 * @param {JSX.Element| Function} Component Component to wrap
 * @param {Object} [options] Options to pass to withQuery
 * @return {JSX.Element}
 */

function withSearchResultsQuery(Component, options) {
  return withQuery(Component, _objectSpread$3({
    hook: useSearchResultsQuery,
    components: components$3
  }, options));
}
/**
 * Use Search Results Query
 * @param {Object} props The props of the parent
 * @param {string} props.query Search Query
 * @param {Object} [props.options] useQuery Options
 * @return {object}
 */

function useSearchResultsQuery() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$query = _ref.query,
      query = _ref$query === void 0 ? '' : _ref$query,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? {
    refetchInterval: query === '' ? refetchInterval$3 : 20000
  } : _ref$options;

  var _useAlgodex = useAlgodex(),
      http = _useAlgodex.http;

  var _useQuery = reactQuery.useQuery(['searchResults', {
    query: query
  }], function () {
    return http.dexd.searchAssets(query);
  }, options),
      data = _useQuery.data,
      isError = _useQuery.isError,
      error = _useQuery.error,
      rest = _objectWithoutProperties(_useQuery, _excluded$3);

  return _objectSpread$3({
    data: data,
    isError: isError,
    error: error
  }, rest);
}

var _excluded$2 = ["data"];

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$2(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var refetchInterval$2 = 3000;
/**
 *
 * @type {{ServiceError, Loading}}
 */

var components$2 = {
  Loading: Spinner,
  ServiceError: ServiceError
};
/**
 *
 * @param {JSX.Element} Component
 * @param {object} [options]
 * @return {JSX.Element}
 */

function withWalletAssetsQuery(Component, options) {
  return withQuery(Component, _objectSpread$2({
    hook: useWalletAssetsQuery,
    components: components$2
  }, options));
}
/**
 * @deprecated
 * @param {object} data
 * @return {null|*}
 */

var mapAssetsData = function mapAssetsData(data) {
  if (!data || !data.allAssets || !data.allAssets.length) {
    return null;
  }

  var assetsData = data.allAssets;
  return assetsData.map(function (_ref) {
    var unit_name = _ref.unit_name,
        name = _ref.name,
        formattedTotalASAAmount = _ref.formattedTotalASAAmount,
        formattedASAAvailable = _ref.formattedASAAvailable,
        formattedASAInOrder = _ref.formattedASAInOrder,
        formattedTotalAlgoEquiv = _ref.formattedTotalAlgoEquiv,
        assetId = _ref.assetId;
    return {
      'unit': unit_name,
      'id': assetId,
      name: name,
      'total': formattedTotalASAAmount || '',
      'available': formattedASAAvailable || '',
      'in-order': formattedASAInOrder || '',
      'algo-value': formattedTotalAlgoEquiv || ''
    };
  });
};
/**
 * Use Wallet Assets Query
 *
 * @param {Object} props The props of the parent
 * @param {Object} props.wallet An instance of a Wallet
 * @param {Object} [props.options] useQuery Options
 * @todo: Fetch Wallet Assets from on-chain
 * @return {object}
 */

function useWalletAssetsQuery(_ref2) {
  var address = _ref2.wallet.address,
      _ref2$options = _ref2.options,
      options = _ref2$options === void 0 ? {
    enabled: typeof address !== 'undefined',
    refetchInterval: refetchInterval$2
  } : _ref2$options;

  var _useAlgodex = useAlgodex(),
      http = _useAlgodex.http;

  var _useQuery = reactQuery.useQuery(['walletAssets', {
    address: address
  }], function () {
    return http.dexd.fetchWalletAssets(address);
  }, options),
      data = _useQuery.data,
      rest = _objectWithoutProperties(_useQuery, _excluded$2);

  var assets = React.useMemo(function () {
    return mapAssetsData(data);
  }, [data]);
  return _objectSpread$2({
    data: {
      assets: assets
    }
  }, rest);
}

/**
 *
 * @param {AlgodexApi} algodex The Algodex API
 * @param {Object} accountInfo
 * @param {boolean} includesFullAccountInfo
 * @return {Promise<number>}
 */

function getMinWalletBalance(_x, _x2) {
  return _getMinWalletBalance.apply(this, arguments);
}
/**
 * Use Wallet Minimum Balance Query
 * @param {Object} props The props of the parent
 * @param {Object} props.wallet An instance of a Wallet
 * @param {Object} [props.options] useQuery Options
 * @return {object}
 */


function _getMinWalletBalance() {
  _getMinWalletBalance = _asyncToGenerator( /*#__PURE__*/regenerator.mark(function _callee2(algodex, accountInfo) {
    var includesFullAccountInfo,
        minBalance,
        numByteSlice,
        _args2 = arguments;
    return regenerator.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            includesFullAccountInfo = _args2.length > 2 && _args2[2] !== undefined ? _args2[2] : false;

            if (includesFullAccountInfo) {
              _context2.next = 11;
              break;
            }

            _context2.prev = 2;
            _context2.next = 5;
            return algodex.http.indexer.fetchAccountInfo(accountInfo.address);

          case 5:
            accountInfo = _context2.sent;
            _context2.next = 11;
            break;

          case 8:
            _context2.prev = 8;
            _context2.t0 = _context2["catch"](2);
            return _context2.abrupt("return", 1000000);

          case 11:
            if (!(!accountInfo || !accountInfo.address)) {
              _context2.next = 13;
              break;
            }

            return _context2.abrupt("return", 1000000);

          case 13:
            logger.debug('in getMinWalletBalance. Checking: ' + accountInfo.address);
            logger.debug({
              accountInfo: accountInfo
            });
            minBalance = 0;

            if (accountInfo['created-apps']) {
              minBalance += 100000 * accountInfo['created-apps'].length; // Apps
            }

            if (accountInfo['assets']) {
              minBalance += accountInfo['assets'].length * 100000;
            }

            if (!isUndefined(accountInfo['apps-total-schema']) && accountInfo['apps-total-schema']['num-uint']) {
              // Total Ints
              minBalance += (25000 + 3500) * accountInfo['apps-total-schema']['num-uint'];
            }

            if (!isUndefined(accountInfo['apps-total-schema']) && accountInfo['apps-total-schema']['num-byte-slice']) {
              numByteSlice = accountInfo['apps-total-schema']['num-byte-slice'];
              minBalance += (25000 + 25000) * numByteSlice; // Total Bytes
            }

            minBalance += 1000000;
            return _context2.abrupt("return", minBalance);

          case 22:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2, null, [[2, 8]]);
  }));
  return _getMinWalletBalance.apply(this, arguments);
}

function useWalletMinBalanceQuery(_ref) {
  var wallet = _ref.wallet,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? {
    enabled: typeof wallet !== 'undefined' && typeof wallet.address !== 'undefined'
  } : _ref$options;

  var _useAlgodex = useAlgodex(),
      algodex = _useAlgodex.algodex;

  return reactQuery.useQuery(['walletMinBalance', {
    address: wallet === null || wallet === void 0 ? void 0 : wallet.address
  }], /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/regenerator.mark(function _callee() {
    return regenerator.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return getMinWalletBalance(algodex, wallet);

          case 2:
            return _context.abrupt("return", _context.sent);

          case 3:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), options);
}

var _excluded$1 = ["data"];

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$1(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var refetchInterval$1 = 3000;
var components$1 = {
  Loading: Spinner,
  ServiceError: ServiceError
};
/**
 *
 * @param {JSX.Element} Component
 * @param {object} [options]
 * @return {JSX.Element}
 */

function withWalletOrdersQuery(Component, options) {
  return withQuery(Component, _objectSpread$1({
    hook: useWalletOrdersQuery,
    components: components$1
  }, options));
}
/**
 * Use Wallet Orders Query
 *
 * @param {Object} props The props of the parent
 * @param {Object} props.wallet An instance of a Wallet
 * @param {Object} [props.options] useQuery Options
 * @return {object}
 */

function useWalletOrdersQuery(_ref) {
  var wallet = _ref.wallet,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? {
    refetchInterval: refetchInterval$1
  } : _ref$options;
  var address = wallet.address;

  var _useAlgodex = useAlgodex(),
      http = _useAlgodex.http; // FIXME: The asset names are also fetched from the search results
  // as a backup method.
  // This is due to some 1.0 server-side issues where the asset names
  // are not always fetched.


  var _useQuery = reactQuery.useQuery(['searchResults'], function () {
    return http.dexd.searchAssets('');
  }, options),
      assetSearchData = _useQuery.data;

  var _useQuery2 = reactQuery.useQuery(['walletOrders', {
    address: address
  }], function () {
    return http.dexd.fetchWalletOrders(address);
  }, options),
      data = _useQuery2.data,
      rest = _objectWithoutProperties(_useQuery2, _excluded$1);

  var orders = React.useMemo(function () {
    return http.dexd.mapOpenOrdersData(data, assetSearchData);
  }, [data, assetSearchData]);
  return _objectSpread$1({
    data: {
      orders: orders
    }
  }, rest);
}

var _excluded = ["data"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var refetchInterval = 3000;
var components = {
  Loading: Spinner,
  ServiceError: ServiceError
};
/**
 *
 * @param {JSX.Element} Component
 * @param {object} [options]
 * @return {JSX.Element}
 */

function withWalletTradeHistoryQuery(Component, options) {
  return withQuery(Component, _objectSpread({
    hook: useWalletTradeHistoryQuery,
    components: components
  }, options));
}
/**
 * Use Wallet Trade History
 *
 * @param {Object} props The props of the parent
 * @param {Object} props.wallet An instance of a Wallet
 * @param {Object} [props.options] useQuery Options
 * @return {object}
 */

function useWalletTradeHistoryQuery(_ref) {
  var wallet = _ref.wallet,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? {
    refetchInterval: refetchInterval
  } : _ref$options;
  var address = wallet.address;

  var _useAlgodex = useAlgodex(),
      http = _useAlgodex.http;

  var _useQuery = reactQuery.useQuery(['walletTradeHistory', {
    address: address
  }], function () {
    return http.dexd.fetchWalletTradeHistory(address);
  }, options),
      data = _useQuery.data,
      rest = _objectWithoutProperties(_useQuery, _excluded);

  var orders = React.useMemo(function () {
    return http.dexd.mapTradeHistoryData(data);
  }, [data]);
  return _objectSpread({
    data: {
      orders: orders
    }
  }, rest);
}

exports.AlgodexContext = AlgodexContext;
exports.FlexContainer = FlexContainer$1;
exports.Provider = Provider;
exports.ServiceError = ServiceError;
exports.Spinner = Spinner;
exports.getBidAskSpread = getBidAskSpread;
exports.getOhlc = getOhlc;
exports.mapAssetsData = mapAssetsData;
exports.mapPriceData = mapPriceData;
exports.mapVolumeData = mapVolumeData;
exports.sortByASAPrice = sortByASAPrice;
exports.sortPriceByTime = sortPriceByTime;
exports.useAlgodex = useAlgodex;
exports.useAlgorandPriceQuery = useAlgorandPriceQuery;
exports.useAssetChartQuery = useAssetChartQuery;
exports.useAssetOrderbookQuery = useAssetOrderbookQuery;
exports.useAssetOrdersQuery = useAssetOrdersQuery;
exports.useAssetPriceQuery = useAssetPriceQuery;
exports.useAssetTradeHistoryQuery = useAssetTradeHistoryQuery;
exports.useExplorerAssetInfo = useExplorerAssetInfo;
exports.useMyAlgoConnect = useMyAlgoConnect;
exports.useSearchResultsQuery = useSearchResultsQuery;
exports.useWalletAssetsQuery = useWalletAssetsQuery;
exports.useWalletConnect = useWalletConnect;
exports.useWalletMinBalanceQuery = useWalletMinBalanceQuery;
exports.useWalletOrdersQuery = useWalletOrdersQuery;
exports.useWalletTradeHistoryQuery = useWalletTradeHistoryQuery;
exports.useWallets = useWallets;
exports.withAlgorandPriceQuery = withAlgorandPriceQuery;
exports.withAssetChartQuery = withAssetChartQuery;
exports.withAssetOrderbookQuery = withAssetOrderbookQuery;
exports.withAssetOrdersQuery = withAssetOrdersQuery;
exports.withAssetPriceQuery = withAssetPriceQuery;
exports.withAssetTradeHistoryQuery = withAssetTradeHistoryQuery;
exports.withExplorerAssetInfo = withExplorerAssetInfo;
exports.withQuery = withQuery;
exports.withSearchResultsQuery = withSearchResultsQuery;
exports.withWalletAssetsQuery = withWalletAssetsQuery;
exports.withWalletOrdersQuery = withWalletOrdersQuery;
exports.withWalletTradeHistoryQuery = withWalletTradeHistoryQuery;
//# sourceMappingURL=index.js.map
